/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { env } from '@/env';
import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { toast } from 'sonner';
import { ensureAntiforgeryToken  } from './api/antiforgery/antiforgery';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
	auth: {
		clientId: env.VITE_MSAL_CLIENT_ID,
		authority:
			'https://login.microsoftonline.com/f1289cc5-8456-4f28-8eab-700d1300fc5d',
		redirectUri: '/',
		postLogoutRedirectUri: '/',
	},
	cache: {
		cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
		storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
	},
};

const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();

// ? Handle redirect promise - this is required for the redirect flow to function
await msalInstance.handleRedirectPromise();

// * Do you currently have an active account? If not, log in.
const accounts = msalInstance.getAllAccounts();
if (accounts.length === 0) {
	await msalInstance.loginRedirect();
}

// * If you have an active account, set it as the active account
const account = accounts[0];
if (account) {
	// Account selection logic is app dependent. Adjust as needed for different use cases.
	msalInstance.setActiveAccount(account);
}

// * Set up fetch to automatically send the token
const token = account.idToken;
const orgFetch = window.fetch;

window.fetch = async function (input: any, init?: RequestInit) {
	if (!init) init = {};
	if (!init.headers) init.headers = {};
	if (!init.credentials) init.credentials = 'include';
	if (token) (init.headers as any).Authorization = `Bearer ${token}`;

	const url = new URL('/api' + input, env.VITE_API_URL);
	const methods = ['POST', 'PUT', 'PATCH', 'DELETE'];
	const method = init.method?.toUpperCase() || 'GET';

	if (methods.includes(method)) {
		try {
			const antiforgery = await ensureAntiforgeryToken();
			(init.headers as any)[antiforgery.headerName] = antiforgery.requestToken;
		} catch (error) {
			console.error('Failed to get antiforgery token:', error);
		}
	}	

	return orgFetch(url, init)
		.then((response) => {
			// Server returned a status code of 2XX
			if (response.ok) {
				// you can call response.json() here if you want to return the json
				return response;
			}

			// Server returned a status code of 4XX or 5XX
			// Throw the response to handle it in the next catch block
			throw response;
		})
		.catch((error) => {
			// It will be invoked for network errors and errors thrown from the then block
			// Do what ever you want to do with error here			
			if (error instanceof Response) {
				// Handle error according to the status code
				switch (error.status) {
					case 400:
					case 403:
						toast.error('Request forbidden', {
							dismissible: true,
							description: 'Your session may have expired. Please re-login.',
						});
						console.log('Request forbidden - possible antiforgery issue.');
						break;
					case 404:
						// You can also call global notification service here
						// to show a notification to the user
						// notificationService.information('Object not found');
						toast.error('Object not found', {
							dismissible: true,
							description:
								'The object you are looking for is not found. This incident has been reported.',
						});
						console.log('Object not found');
						break;
					case 500:
						// notificationService.error('Internal server error');
						toast.error('Internal server error', {
							dismissible: true,
							description:
								'An internal server error occurred. This incident has been reported.',
						});
						console.log('Internal server error');
						break;
					default:
						// notificationService.error('Some error occurred');
						toast.error('Some error occurred', {
							dismissible: true,
							description: 'An error occurred. This incident has been reported.',
						});
						console.log('Some error occurred');
						break;
				}
			} else {
				// Handle network errors
				// notificationService.error('Network error');
				toast.error('Network error', {
					dismissible: true,
					description: 'A network error occurred. This incident has been reported.',					
				});
				console.log('Network error');
				console.log(error);
			}
			// In case you don't want to throw the error just return some value here
			// This will be returned to the then block as a resolved promise
			// return { success: false };

			// Here you can throw a custom error too
			// throw new Error('Something went wrong');
			throw error;
		});
};

// Optional - This will update account state if a user signs in from another tab or window
msalInstance.enableAccountStorageEvents();

// Listen for sign-in event and set active account
msalInstance.addEventCallback((event) => {
	if (
		event.eventType === EventType.LOGIN_SUCCESS &&
		(event.payload as any).account
	) {
		const account = (event.payload as any).account!;
		msalInstance.setActiveAccount(account);
	}
});

export { msalInstance };
